import React, { useEffect, useState, useCallback, useMemo } from "react";
import { usePersistedState } from "@/lib/usePersistedState";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FilePlus2, Inbox, MessageSquare, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { StatusPill, STATUS_META } from "@/lib/changeForm";
import { fmtDate } from "@/components/DatePicker";

export default function ChangeRequests() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [scope, setScope] = useState("all");
  const [unread, setUnread] = useState({});
  const isAdmin = user.role === "bureau_admin";

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (isAdmin && scope === "active" && activeCompanyId) params.company_id = activeCompanyId;
    api.get("/change-requests", { params }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/change-requests/unread", { params: isAdmin && scope === "active" ? { company_id: activeCompanyId } : {} }).then((r) => setUnread(r.data.per_request || {})).catch(() => {});
    api.get("/change-requests/summary", { params: isAdmin && scope === "active" ? { company_id: activeCompanyId } : {} }).then((r) => setSummary(r.data)).catch(() => {});
  }, [status, scope, activeCompanyId, isAdmin]);
  useEffect(() => { load(); }, [load]);
  const [open, setOpen] = usePersistedState("cr-tree", {});
  const tree = useMemo(() => {
    const ys = {};
    rows.forEach((r) => { const d = (r.updated_at || r.created_at || "").slice(0, 10); const y = d.slice(0, 4) || "—", m = d.slice(0, 7) || "—", e = r.form?.employee_name || "—"; ys[y] = ys[y] || {}; ys[y][m] = ys[y][m] || {}; (ys[y][m][e] = ys[y][m][e] || []).push(r); });
    return Object.keys(ys).sort().reverse().map((y) => ({ key: y, months: Object.keys(ys[y]).sort().reverse().map((m) => ({ key: m, label: new Date(`${m}-15T00:00:00`).toLocaleString("en-US", { month: "long" }), emps: Object.keys(ys[y][m]).sort((a, b) => a.localeCompare(b)).map((e) => ({ key: `${m}|${e}`, name: e, rows: ys[y][m][e].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")) })) })) }));
  }, [rows]);
  const isOpen = (k, d) => (k in open ? open[k] : d);
  const setAll = (v) => { const o = {}; tree.forEach((y) => { o[`y|${y.key}`] = v; y.months.forEach((m) => { o[`m|${m.key}`] = v; m.emps.forEach((e) => { o[`e|${e.key}`] = v; }); }); }); setOpen(o); };
  const toggle = (k, d) => setOpen((o) => ({ ...o, [k]: !isOpen(k, d) }));
  const countOf = (ms) => ms.reduce((a, m) => a + m.emps.reduce((b, e) => b + e.rows.length, 0), 0);
  const cols = isAdmin ? 8 : 7;
  const GroupRow = ({ k, d, level, title, count, testid }) => (
    <tr data-testid={testid} className={`cursor-pointer ${level === 0 ? "bg-[#0b1a3a] text-white hover:bg-[#12285a]" : level === 1 ? "bg-muted hover:bg-muted/70" : "hover:bg-accent"}`} onClick={() => toggle(k, d)}>
      <td colSpan={cols} className={`${level === 0 ? "p-2.5" : level === 1 ? "p-2 pl-6 font-semibold" : "p-1.5 pl-10 text-sm"}`}><span className="inline-flex items-center gap-2">{isOpen(k, d) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}<span className={level === 0 ? "font-display font-bold" : ""}>{title}</span><span className={`text-xs font-normal ${level === 0 ? "text-slate-300" : "text-muted-foreground"}`}>{count} request{count !== 1 ? "s" : ""}</span></span></td>
    </tr>
  );
  const remove = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the change request for ${r.form.employee_name}? This cannot be undone.`)) return;
    try { await api.delete(`/change-requests/${r.id}`); toast.success("Change request deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="change-requests-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{isAdmin ? "Change Requests Inbox" : "Payroll Change Requests"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{isAdmin ? "Payroll change forms submitted by your clients, awaiting processing." : "Submit and track payroll change forms sent to your payroll manager."}</p>
        </div>
        <Button data-testid="new-change-request-btn" onClick={() => navigate("/change-requests/new")}><FilePlus2 className="w-4 h-4 mr-1.5" /> New Change Form</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="cr-summary">
          {Object.keys(STATUS_META).map((s) => (
            <button key={s} data-testid={`cr-filter-${s}`} onClick={() => setStatus(status === s ? "all" : s)}
              className={`text-left rounded-xl border p-3 transition-colors ${status === s ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
              <div className="text-xs text-muted-foreground">{STATUS_META[s].label}</div>
              <div className="font-mono-num text-2xl font-bold">{summary[s]}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 items-center flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="cr-status-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" data-testid="cr-expand-all" onClick={() => setAll(true)}>Expand all</Button>
        <Button variant="outline" size="sm" data-testid="cr-collapse-all" onClick={() => setAll(false)}>Collapse all</Button>
        {isAdmin && (
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="cr-scope-select" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All clients</SelectItem><SelectItem value="active">Selected client only</SelectItem></SelectContent>
          </Select>
        )}
      </div>

      <Card className="overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground"><Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />No change requests {status !== "all" ? `with status "${STATUS_META[status]?.label}"` : "yet"}.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground text-left bg-muted/50">
              <tr><th className="p-3">Employee</th>{isAdmin && <th className="p-3">Client</th>}<th className="p-3">Change type</th><th className="p-3">Effective</th><th className="p-3">Submitted by</th><th className="p-3">Status</th><th className="p-3">Updated</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {tree.map((y, yi) => (<React.Fragment key={y.key}>
                <GroupRow k={`y|${y.key}`} d={yi === 0} level={0} title={y.key} count={countOf(y.months)} testid={`cr-year-${y.key}`} />
                {isOpen(`y|${y.key}`, yi === 0) && y.months.map((m, mi) => (<React.Fragment key={m.key}>
                  <GroupRow k={`m|${m.key}`} d={yi === 0 && mi === 0} level={1} title={m.label} count={countOf([m])} testid={`cr-month-${m.key}`} />
                  {isOpen(`m|${m.key}`, yi === 0 && mi === 0) && m.emps.map((e) => (<React.Fragment key={e.key}>
                    <GroupRow k={`e|${e.key}`} d={true} level={2} title={e.name} count={e.rows.length} testid={`cr-emp-${e.key}`} />
                    {isOpen(`e|${e.key}`, true) && e.rows.map((r) => (
                <tr key={r.id} data-testid={`cr-row-${r.id}`} className="border-t border-border hover:bg-accent/50 cursor-pointer" onClick={() => navigate(`/change-requests/${r.id}`)}>
                  <td className="p-3 font-medium"><Link to={`/change-requests/${r.id}`} className="hover:underline">{r.form.employee_name}</Link>{unread[r.id] > 0 && <span data-testid={`cr-unread-${r.id}`} title={`${unread[r.id]} new employee repl${unread[r.id] === 1 ? "y" : "ies"}`} className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white"><MessageSquare className="w-3 h-3" /> {unread[r.id]}</span>}{r.source === "employee" && <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-primary/10 text-primary">self-service</span>}<div className="text-xs text-muted-foreground">{r.form.job_title}</div></td>
                  {isAdmin && <td className="p-3">{r.company_logo} {r.company_name}</td>}
                  <td className="p-3 text-xs">{(r.form.change_types || []).slice(0, 2).join(", ")}{(r.form.change_types || []).length > 2 && ` +${r.form.change_types.length - 2}`}</td>
                  <td className="p-3 font-mono-num text-xs">{fmtDate(r.form.effective_date) || "—"}</td>
                  <td className="p-3 text-xs">{r.submitted_by?.name}</td>
                  <td className="p-3"><StatusPill status={r.status} /></td>
                  <td className="p-3 font-mono-num text-xs text-muted-foreground">{fmtDate(r.updated_at)}</td>
                  <td className="p-3 text-right"><Button variant="ghost" size="icon" className="text-destructive" title="Delete request" data-testid={`cr-delete-${r.id}`} onClick={(e) => remove(e, r)}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
                    ))}
                  </React.Fragment>))}
                </React.Fragment>))}
              </React.Fragment>))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
